import './App.css';

import React from 'react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from "react-router-dom";
import {
  defaults,
  device,
  features as webstoreFeatures,
  properties,
  services as selectServices,
  splashOncePerSession,
  splashPageUrl
} from './redux/Selectors'

import {
  features,
  fetchAllEditionParts,
  fetchChannels,
  fetchClassmarketsConfig,
  fetchCountries,
  fetchEditions,
  fetchImmomarktConfig,
  fetchMarkets,
  fetchOffers,
  fetchOrderTypes,
  fetchPageDimensions,
  fetchPaymentTypes,
  fetchProperties,
  fetchSalutations,
  fetchTemplates,
  fetchTitles,
  getAuthentication,
  getAuthProvider,
  services,
  setDevice,
  urlParams
} from './redux/Actions.js';
import { useNavigate } from 'react-router-dom';
import './configuration/i18n';
import Splash from './pages/Splash';
import { getPage } from './common/navigationTools';

/**
 * ZEN-29183: Merker, dass die Kundenseite in dieser Browser-Session schon gezeigt wurde.
 */
const SPLASH_SEEN_KEY = 'ws.splash.seen';

const readSessionFlag = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeSessionFlag = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (e) {
    // sessionStorage nicht verfuegbar (private Modi, Browser-Einstellungen) - ignorieren
  }
};

const App = ({ theme }) => {

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loadMasterData = async () => {
    await dispatch(fetchMarkets());
    await dispatch(fetchTemplates());
    await dispatch(getAuthProvider());
    await dispatch(fetchOffers());
    await dispatch(fetchEditions());
    dispatch(getAuthentication());
    dispatch(fetchAllEditionParts());
    dispatch(fetchPaymentTypes());
    dispatch(fetchSalutations());
    dispatch(fetchTitles());
    dispatch(fetchCountries());
    dispatch(fetchOrderTypes());
    dispatch(fetchChannels());
    dispatch(fetchImmomarktConfig());
    dispatch(fetchPaymentTypes());
    dispatch(fetchClassmarketsConfig());
    dispatch(fetchPageDimensions());
  }

  const _defaults = useSelector((state) => defaults(state));
  const _device = useSelector((state) => device(state));
  const _properties = useSelector((state) => properties(state));
  const _selectServices = useSelector((state) => selectServices(state));
  const _webstoreFeatures = useSelector((state) => webstoreFeatures(state));
  const _splashPageUrl = useSelector((state) => splashPageUrl(state));
  const _splashOncePerSession = useSelector((state) => splashOncePerSession(state));

  // Stammdaten geladen?
  const [masterDataReady, setMasterDataReady] = useState(false);
  // ZEN-29183: null = noch nicht geprueft, '' = keine Kundenseite, sonst deren URL
  const [splashPage, setSplashPage] = useState(null);
  // ZEN-29183: Kundenseite vom Benutzer explizit geschlossen?
  const [splashClosed, setSplashClosed] = useState(false);

  /**
   * Verlaesst die React-UI bzw. springt auf die Startseite. Frueher direkt im
   * Splash-Timeout, jetzt eigene Funktion, weil der Zeitpunkt vom Splash-Modus abhaengt.
   */
  const finishSplash = () => {
    if (!_webstoreFeatures.WEBSTORE_NG.active) {
      window.location = '/webstore';
    }
    else if ('mini' === _device) {
      navigate('/webstore/app/min-width');
    }
    else if ('mobile' !== _device && _webstoreFeatures.MOBILE_ONLY.active) {
      window.location = '/webstore';
    } else {
      navigate(getPage('start', _device));
    }
    /*       else if ('mobile' === _device) {
             navigate('/webstore/app/m/markets');
           }
           else {
             navigate('/webstore/app/templates');
           } */
  };

  /**
   * ZEN-29183: Wird die React-UI ohnehin sofort verlassen (Polymer-Fallback, min-width),
   * hat die Kundenseite keinen Sinn.
   */
  const leavesReactUi = () => {
    if (!_webstoreFeatures || !_webstoreFeatures.WEBSTORE_NG) {
      return false;
    }
    return !_webstoreFeatures.WEBSTORE_NG.active
      || 'mini' === _device
      || ('mobile' !== _device && _webstoreFeatures.MOBILE_ONLY.active);
  };

  const showSplashPage = !!splashPage && !leavesReactUi();

  const closeSplashPage = () => {
    if (_splashOncePerSession) {
      writeSessionFlag(SPLASH_SEEN_KEY, 'true');
    }
    setSplashClosed(true);
  };

  useEffect(() => {

    if (_properties && Object.keys(_properties).length) {
      loadMasterData().then(() => {
        setMasterDataReady(true);
      });
    }
  }, [_properties]);

  /**
   * ZEN-29183: Pruefen, ob eine Kundenseite konfiguriert und auch auslieferbar ist.
   * Fehlt die hochgeladene HTML-Ressource (404), bleibt es beim klassischen Splash.
   */
  useEffect(() => {
    let cancelled = false;

    if (!_splashPageUrl) {
      setSplashPage('');
      return undefined;
    }
    if (_splashOncePerSession && 'true' === readSessionFlag(SPLASH_SEEN_KEY)) {
      setSplashPage('');
      return undefined;
    }

    fetch(_splashPageUrl, { credentials: 'same-origin' })
      .then((response) => {
        if (!cancelled) {
          if (!response.ok) {
            console.warn(`Splash-Seite ${_splashPageUrl} nicht verfuegbar (${response.status}).`);
          }
          setSplashPage(response.ok ? _splashPageUrl : '');
        }
      })
      .catch((e) => {
        console.warn(`Splash-Seite ${_splashPageUrl} nicht erreichbar.`, e);
        if (!cancelled) {
          setSplashPage('');
        }
      });

    return () => { cancelled = true; };
  }, [_splashPageUrl, _splashOncePerSession]);

  /**
   * Weiterschalten: ohne Kundenseite wie bisher nach 'splashDuration',
   * mit Kundenseite (ZEN-29183) erst nach dem expliziten Schliessen.
   */
  useEffect(() => {
    if (!masterDataReady || null === splashPage) {
      return undefined;
    }

    if (showSplashPage) {
      if (splashClosed) {
        finishSplash();
      }
      return undefined;
    }

    let splashTimeout = (Number(_properties['splashDuration']) || 3) * 1000;
    const timer = setTimeout(() => finishSplash(), splashTimeout);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [masterDataReady, splashPage, splashClosed]);

  useEffect(() => {
    if (_webstoreFeatures) {
      dispatch(fetchProperties());
    }
  }, [_webstoreFeatures]);

  useEffect(() => {
    if (_defaults && Object.keys(_defaults).length) {
      dispatch(features());
    }
  }, [_defaults]);

  useEffect(() => {
    if (_selectServices) {
      dispatch(urlParams());
    }
  }, [_selectServices]);

  useEffect(() => {
    dispatch(services());
  }, []);

  return (
    <Splash
      pageUrl={showSplashPage ? splashPage : ''}
      ready={masterDataReady}
      onClose={closeSplashPage}>
    </Splash>
  );
}

export default App;